import React from 'react';
import cn from '../utils/cn';
import {
  controlHeights,
  inputFrameClasses,
  inputRoundedClasses,
} from './KoobInputText';

type KoobInputStarsProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'className' | 'onChange'
> & {
  // not part of the API (see packages/ui/README.md); stripped below
  className?: never;
  value?: number | null;
  onValueChange?: (value: number | null) => void;
  max?: number;
  /** Render unfilled stars up to `max`, so 0 shows them all grey. Defaults to true. */
  showEmpty?: boolean;
  /** Star size. Defaults to `md` when interactive, `lg` otherwise. */
  size?: keyof typeof starSizes;
  /** Show a clear button once a value is set (interactive only). */
  clearable?: boolean;
  disabled?: boolean;
  rounded?: 'md' | 'full';
};

const starSizes = { xs: 12, sm: 14, md: 18, lg: 24 };

const KoobInputStars = React.forwardRef<HTMLDivElement, KoobInputStarsProps>(
  (
    {
      value,
      onValueChange,
      max = 5,
      showEmpty = true,
      size,
      clearable = false,
      disabled = false,
      rounded = 'md',
      // className is not part of the API (see packages/ui/README.md)
      className,
      ...props
    },
    ref,
  ) => {
    const interactive = Boolean(onValueChange);
    const shown = showEmpty ? max : (value ?? 0);
    const fontSize = starSizes[size ?? (interactive ? 'md' : 'lg')];

    return (
      <div
        ref={ref}
        className={cn(
          'inline-flex items-center',
          // Interactive mode is a form control: wear the shared field frame
          // so it lines up with the text/select/date inputs around it.
          interactive &&
            `${inputFrameClasses} ${controlHeights.md} gap-0.5 ${inputRoundedClasses[rounded]} px-3`,
          interactive && disabled && 'opacity-50',
        )}
        role={interactive ? undefined : 'img'}
        aria-label={interactive ? undefined : `${value ?? 0} / ${max}`}
        {...props}
      >
        {Array.from({ length: shown }, (_, index) => {
          const filled = (value ?? 0) >= index + 1;
          const star = (
            <i
              className={`fa-solid fa-star ${filled ? 'text-[#ffd500]' : 'text-gray-300'}`}
              style={{ fontSize }}
            />
          );

          return interactive ? (
            <button
              key={index}
              type="button"
              disabled={disabled}
              onClick={() => onValueChange?.(index + 1)}
              aria-label={`${index + 1} / ${max}`}
              className={cn(
                'inline-flex items-center border-none bg-transparent p-0 cursor-pointer disabled:cursor-default',
                !filled && 'hover:[&_svg]:text-gray-400',
              )}
            >
              {star}
            </button>
          ) : (
            <span key={index} className="inline-flex items-center">
              {star}
            </span>
          );
        })}
        {clearable && interactive && Boolean(value) && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onValueChange?.(null)}
            aria-label="clear"
            className="ml-1 inline-flex items-center border-none bg-transparent p-0 cursor-pointer text-gray-400 hover:text-gray-600 disabled:cursor-default"
          >
            <i className="fa-light fa-circle-xmark" style={{ fontSize: 16 }} />
          </button>
        )}
      </div>
    );
  },
);

KoobInputStars.displayName = 'KoobInputStars';

export default KoobInputStars;
